import { Injectable, ElementRef } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import isEmpty from 'lodash-es/isEmpty';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment as ENV } from '@environment';
import {
  PERSON_FORM_INPUTS, INVALID, ND, PERSON_ADDRESS_FORM_INPUTS, BOOKING_FORM_INPUTS
} from '@booking/constants/forms.constants';
import { BillingPersonType } from '@booking/entities/billing-person-types';
import { User } from '@app/modules/core/interfaces/user';
import { BillingResponse, BillingPersonalInfo, Address, BillingCity } from '@billing/entities/billing.payload';
import { UserGender } from '@booking/entities/user-gender';
import { UserAddresses, Address as CoreAddress } from '@app/modules/core/interfaces/address';
import { findCityByName } from '@billing/mappers/cities.mapper';
import { CountriesVm } from '@booking/entities/countries';
import { ScrollFormService } from '@reservation/services/scroll-form.service';
import { whitespaceValidator } from '@reservation/validators/whitespace-validator';
import { FormInputsService } from '@reservation/services/form-inputs.service';
import { replaceDiacritics } from '@shared/directives/mappers/replace-diacritics';
import { TravelerPaxResponse } from '@pax/entities/traveler-pax';
import { InputRegex } from '@forms/form-text-input/entities/input-mask';
import { BookingBillingTypeDocumentProps } from '@app/entities/booking-documents';
import { defaultBillingDocumentId, defaultBillingDocumentNumber } from '@forms/mappers/form-document-input.mapper';
import { AssetBookingConfig } from '@asset-booking-config';

@Injectable()
export class BillingFormService {

  constructor(
    private formInputsService: FormInputsService,
    private scrollFormService: ScrollFormService
  ) { }

  get stringAddresCodeValidators(): ValidatorFn[] {
    return [
      Validators.required, Validators.minLength(2),
      Validators.maxLength(15), whitespaceValidator,
    ];
  }

  get documentValidator(): ValidatorFn[] {
    return [Validators.required, whitespaceValidator, Validators.minLength(3)];
  }

  get documentIdValidator(): ValidatorFn[] {
    return [Validators.required];
  }

  get defaultDocumentValidator(): ValidatorFn[] {
    return [...this.documentValidator, Validators.maxLength(15), Validators.pattern(InputRegex.NUMERIC)];
  }

  public isNaturalPerson(type: BillingPersonType): boolean {
    return type === BillingPersonType.NATURAL_PERSON;
  }
  public hasUserInfo(user: User): boolean {
    return !!user && !isEmpty(user);
  }
  public hasDefaultBillingInfo(billingInfo: BillingResponse): boolean {
    return !!billingInfo && !isEmpty(billingInfo);
  }
  public scrollIntoView(elementRef: ElementRef): void {
    this.scrollFormService.scrollIntoView(elementRef);
  }
  public hasPaxSelection(pax: TravelerPaxResponse): boolean {
    return !!pax && !isEmpty(pax);
  }

  public userGender(gender: string): UserGender {
    return gender === UserGender.Male ? UserGender.Male : gender === UserGender.Female ? UserGender.Female : null;
  }

  public changeUpperCasePersonNames(form: UntypedFormGroup, value: string, inputType: string): void {
    switch (inputType) {
      case PERSON_FORM_INPUTS.FIRST_NAME:
        form.patchValue({ [PERSON_FORM_INPUTS.FIRST_NAME]: this.formInputsService.capitalizeValue(value) });
        break;
      case PERSON_FORM_INPUTS.SECOND_NAME:
        form.patchValue({ [PERSON_FORM_INPUTS.SECOND_NAME]: this.formInputsService.capitalizeValue(value) });
        break;
      case PERSON_FORM_INPUTS.LAST_NAME:
        form.patchValue({ [PERSON_FORM_INPUTS.LAST_NAME]: this.formInputsService.capitalizeValue(value) });
        break;
      case PERSON_FORM_INPUTS.LEGAL_NAME:
        form.patchValue({ [PERSON_FORM_INPUTS.LEGAL_NAME]: value });
        break;
      case PERSON_ADDRESS_FORM_INPUTS.STREET:
        form.patchValue({ [PERSON_ADDRESS_FORM_INPUTS.STREET]: this.formInputsService.capitalizeValue(value) });
        break;
      default: break;
    }
  }

  public isFieldInvalid(field: string, formValidation: UntypedFormGroup, isSubmitted: boolean): boolean {
    return formValidation && formValidation.get(field) ?
      (!formValidation.get(field).valid || formValidation.get(field).status === INVALID) && isSubmitted : false;
  }

  public addErrorClassBilling(field: string, formValidation: UntypedFormGroup, isSubmitted: boolean): { [key: string]: boolean } {
    return this.displayFieldCss(field, formValidation, isSubmitted);
  }

  public formInvalidControls(formValidation: UntypedFormGroup): boolean {
    const controls = formValidation.controls;
    for (const control in controls) {
      if (controls[control].status === INVALID) return true;
    } return false;
  }

  public countryCode(user: User, defaultBilling: BillingResponse, countries: CountriesVm[]): string {
    return this.hasDefaultBillingInfo(defaultBilling) && defaultBilling.billing_info.address.country_code ?
      defaultBilling.billing_info.address.country_code : this.defaultCountry(countries, user);
  }

  public defaultCityCode$(
    defaultBilling: BillingResponse, userAddresses: UserAddresses, cities: BillingCity[], country: string
  ): Observable<string> {
    return this.hasDefaultBillingInfo(defaultBilling) && defaultBilling.billing_info.address.city_code ?
      of(defaultBilling.billing_info.address.city_code) : userAddresses && userAddresses.activeAddress ?
        userAddresses.activeAddress.country === country ?
          this.cityCode$(userAddresses.activeAddress, cities).pipe(map(code => code)) : of('') : of('');
  }

  public defaultNaturalPersonForm(
    documents: BookingBillingTypeDocumentProps[],
    user: User,
    billing: BillingResponse,
    bookingForm: UntypedFormGroup,
    pax: TravelerPaxResponse
  ): BillingPersonalInfo {
    const bookingFormValues = bookingForm?.value;
    if (!isEmpty(bookingFormValues)) {
      const hasDefaultBillingInfo = this.hasDefaultBillingInfo(billing);
      const hasPaxSelection = this.hasPaxSelection(pax);
      const hasUserInfo = this.hasUserInfo(user);
      return {
        [PERSON_FORM_INPUTS.LEGAL_NAME]: ND,
        [PERSON_FORM_INPUTS.NATURAL_PERSON]: true,
        [PERSON_FORM_INPUTS.FIRST_NAME]: hasDefaultBillingInfo ? billing.billing_info.first_name :
          hasPaxSelection ? pax.first_name : hasUserInfo ? this.formInputsService.userFirstName(user) : '',
        [PERSON_FORM_INPUTS.SECOND_NAME]: hasDefaultBillingInfo ? billing.billing_info.middle_name :
          hasPaxSelection ? pax.middle_name : hasUserInfo ? this.formInputsService.userSecondName(user) : '',
        [PERSON_FORM_INPUTS.LAST_NAME]: hasDefaultBillingInfo ? billing.billing_info.last_name :
          hasPaxSelection ? pax.last_name : hasUserInfo ? replaceDiacritics(user.last_name) : '',
        [PERSON_FORM_INPUTS.GENDER]: UserGender.Male,
        [PERSON_FORM_INPUTS.DOCUMENT_NUMBER]: defaultBillingDocumentNumber(documents, user, billing, pax,
          hasDefaultBillingInfo, hasPaxSelection, hasUserInfo),
        [PERSON_FORM_INPUTS.DOCUMENT_ID]: defaultBillingDocumentId(documents, user, billing, pax,
          hasDefaultBillingInfo, hasUserInfo, hasPaxSelection),
        [PERSON_FORM_INPUTS.PHONE]: hasDefaultBillingInfo ? billing.billing_info.phone_number :
          bookingFormValues[BOOKING_FORM_INPUTS.PHONE],
        [PERSON_FORM_INPUTS.EMAIL]: hasDefaultBillingInfo ? billing.billing_info.email_address :
          bookingFormValues[BOOKING_FORM_INPUTS.EMAIL]
      } as BillingPersonalInfo;

    } else return {} as BillingPersonalInfo;
  }

  public defaultBillingPersonAddress(userAddresses: UserAddresses, defaultBilling: BillingResponse, user: User,
    cities: BillingCity[], countries: CountriesVm[]): Address {
    return {
      [PERSON_ADDRESS_FORM_INPUTS.STREET]: this.hasDefaultBillingInfo(defaultBilling) ?
        defaultBilling.billing_info.address.street : userAddresses && userAddresses.activeAddress ?
          userAddresses.activeAddress.address : '',
      [PERSON_ADDRESS_FORM_INPUTS.CITY_CODE]: this.hasDefaultBillingInfo(defaultBilling) ?
        defaultBilling.billing_info.address.city_code : '',
      [PERSON_ADDRESS_FORM_INPUTS.COUNTRY_CODE]: this.countryCode(user, defaultBilling, countries),
      [PERSON_ADDRESS_FORM_INPUTS.POSTAL_CODE]: this.hasDefaultBillingInfo(defaultBilling) ?
        defaultBilling.billing_info.address.postal_code : '',
      [PERSON_ADDRESS_FORM_INPUTS.STATE_CODE]: this.hasDefaultBillingInfo(defaultBilling) ?
        defaultBilling.billing_info.address.state_code : (AssetBookingConfig.BILLING_MODULE.ENABLED_FULL_ADDRESS_FORM ? '' : ND),
      [PERSON_ADDRESS_FORM_INPUTS.NEIGHBORHOOD]: this.hasDefaultBillingInfo(defaultBilling) ?
        defaultBilling.billing_info.address.neighborhood : '',
      [PERSON_ADDRESS_FORM_INPUTS.COMPLEMENT]: this.hasDefaultBillingInfo(defaultBilling) ?
        defaultBilling.billing_info.address.complement : '',
    } as Address;
  }

  public validateSectionFormToScroll(form: UntypedFormGroup, refs: { [key: string]: ElementRef } = {}): void {
    const addressForm = form.get(PERSON_FORM_INPUTS.ADDRESS) as UntypedFormGroup;
    if (this.validateSectionForm([PERSON_FORM_INPUTS.FIRST_NAME, PERSON_FORM_INPUTS.SECOND_NAME], form)) {
      this.scrollIntoView(refs[PERSON_FORM_INPUTS.FIRST_NAME]); return;
    } else if (this.validateSectionForm([PERSON_FORM_INPUTS.LAST_NAME], form)) {
      this.scrollIntoView(refs[PERSON_FORM_INPUTS.LAST_NAME]); return;
    } else if (this.validateSectionForm([PERSON_FORM_INPUTS.LEGAL_NAME], form)) {
      this.scrollIntoView(refs[PERSON_FORM_INPUTS.LEGAL_NAME]); return;
    } else if (this.validateSectionForm([PERSON_FORM_INPUTS.DOCUMENT_NUMBER, PERSON_FORM_INPUTS.DOCUMENT_ID], form)) {
      this.scrollIntoView(refs[PERSON_FORM_INPUTS.DOCUMENT_ID]); return;
    } else if (addressForm && this.validateSectionForm([PERSON_ADDRESS_FORM_INPUTS.STREET], addressForm)) {
      this.scrollIntoView(refs[PERSON_ADDRESS_FORM_INPUTS.STREET]); return;
    } if (addressForm && this.validateSectionForm([PERSON_ADDRESS_FORM_INPUTS.COUNTRY_CODE], addressForm)) {
      this.scrollIntoView(refs[PERSON_ADDRESS_FORM_INPUTS.COUNTRY_CODE]); return;
    } else if (addressForm && this.validateSectionForm([PERSON_ADDRESS_FORM_INPUTS.CITY_CODE], addressForm)) {
      this.scrollIntoView(refs[PERSON_ADDRESS_FORM_INPUTS.CITY_CODE]); return;
    } else if (this.validateSectionForm([PERSON_FORM_INPUTS.EMAIL], form)) {
      this.scrollIntoView(refs[PERSON_FORM_INPUTS.EMAIL]); return;
    }
  }

  public addNaturalPersonFiscalResponsibility(form: UntypedFormGroup, value: string): void {
    form.addControl(PERSON_FORM_INPUTS.FISCAL_RESPONSIBILITY, new UntypedFormControl(value));
  }

  public getTranslate(key: string | string[]): string | string[] {
    return this.formInputsService.getTranslate(key);
  }

  private validateSectionForm(controls: string[], form: UntypedFormGroup): boolean {
    return controls.map(control => form.controls[control].status === INVALID).filter(result => result).length > 0;
  }

  private cityCode$(address: CoreAddress, cities: BillingCity[]): Observable<string> {
    return of(findCityByName(address, cities));
  }

  private defaultCountry(countries: CountriesVm[], user: User): string {
    const defaultCountryIndex = this.defaultCountryIndex(countries, user);
    return defaultCountryIndex > -1 ? countries[defaultCountryIndex].code : '';
  }

  private defaultCountryIndex(countries: CountriesVm[], user: User): number {
    return countries.findIndex(c => c.code === ENV.countryInfo.iso_code);
  }

  private displayFieldCss(field: string, formValidation: UntypedFormGroup, isSubmitted: boolean): { [key: string]: boolean } {
    return { error: this.isFieldInvalid(field, formValidation, isSubmitted) };
  }

}
